import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';

type DashboardStat = {
  label: string;
  suffix?: string;
  icon: string;
  tone: 'blue' | 'green' | 'red' | 'amber' | 'violet';
};

type DashboardStatWithValue = DashboardStat & {
  value: string;
};

type GeneratingCompanyRow = {
  code: string;
  name: string;
  qcaCode: string;
  qcaName: string;
  pssCode: string;
  pssName: string;
  technology: 'Solar' | 'Wind' | 'Hybrid';
  ratedCapacityMw: number;
  availableCapacityMw: number;
  commissionDate: string;
  status: 'Operational' | 'Under Outage' | 'Standby';
  operationalSince: string;
  remarks: string;
};

type GeneratorUnit = {
  code: string;
  name: string;
  technology: 'Solar' | 'Wind' | 'Hybrid';
  ratedCapacityMw: number;
  status: 'Operational' | 'Under Outage' | 'Standby';
  commissionDate: string;
};

type GeneratingCompanyManagementMock = {
  header: {
    title: string;
    description: string;
  };
  stats: DashboardStat[];
  filters: {
    qca: string[];
    pss: string[];
    technology: string[];
    status: string[];
  };
  rows: GeneratingCompanyRow[];
  generatorCatalog: GeneratorUnit[];
  companyGeneratorMappings: Record<string, string[]>;
};

const DEFAULT_DATA: GeneratingCompanyManagementMock = {
  header: {
    title: 'Manage Generating Companies',
    description: 'Review generating company registrations and manage linked generators.'
  },
  stats: [],
  filters: {
    qca: ['All'],
    pss: ['All'],
    technology: ['All'],
    status: ['All']
  },
  rows: [],
  generatorCatalog: [],
  companyGeneratorMappings: {}
};

@Component({
  selector: 'app-generating-company-registration',
  standalone: true,
  templateUrl: './generating-company-registration.component.html',
  styleUrls: ['./generating-company-registration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeneratingCompanyRegistrationComponent {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly pageData = signal<GeneratingCompanyManagementMock>(DEFAULT_DATA);
  readonly searchTerm = signal('');
  readonly selectedQca = signal('All');
  readonly selectedPss = signal('All');
  readonly selectedTechnology = signal('All');
  readonly selectedStatus = signal('All');
  readonly minCapacity = signal('');
  readonly maxCapacity = signal('');
  readonly currentPage = signal(1);
  readonly pageSize = signal(8);
  readonly isManageGeneratorsModalOpen = signal(false);
  readonly selectedCompanyCode = signal<string | null>(null);
  readonly activeGeneratorTab = signal<'available' | 'linked'>('available');
  readonly availableGeneratorSearch = signal('');
  readonly linkedGeneratorSearch = signal('');
  readonly selectedAvailableGeneratorCodes = signal<string[]>([]);
  readonly selectedLinkedGeneratorCodes = signal<string[]>([]);

  readonly title = computed(() => this.route.snapshot.data['title'] as string || this.pageData().header.title);
  readonly description = computed(() => this.route.snapshot.data['description'] as string || this.pageData().header.description);
  readonly stats = computed<DashboardStatWithValue[]>(() => {
    const rows = this.rows();
    const totalCapacity = rows.reduce((total, row) => total + row.ratedCapacityMw, 0);
    const availableCapacity = rows.reduce((total, row) => total + row.availableCapacityMw, 0);
    const values: Record<string, string> = {
      'Total Companies': String(rows.length),
      'Operational Companies': String(rows.filter((row) => row.status === 'Operational').length),
      'Under Outage': String(rows.filter((row) => row.status === 'Under Outage').length),
      'Standby Companies': String(rows.filter((row) => row.status === 'Standby').length),
      'Total Capacity': this.formatCapacity(totalCapacity),
      'Available Capacity (AvC)': this.formatCapacity(availableCapacity)
    };

    return this.pageData().stats.map((card) => ({
      ...card,
      value: values[card.label] ?? '0'
    }));
  });
  readonly filters = computed(() => this.pageData().filters);
  readonly rows = computed(() => this.pageData().rows);
  readonly generatorCatalog = computed(() => this.pageData().generatorCatalog);
  readonly selectedCompany = computed(() => this.rows().find((row) => row.code === this.selectedCompanyCode()) || null);
  readonly linkedGenerators = computed(() => {
    const companyCode = this.selectedCompanyCode();
    if (!companyCode) {
      return [];
    }
    const linkedCodes = new Set(this.pageData().companyGeneratorMappings[companyCode] || []);
    return this.generatorCatalog().filter((item) => linkedCodes.has(item.code));
  });
  readonly availableGenerators = computed(() => {
    const companyCode = this.selectedCompanyCode();
    if (!companyCode) {
      return [];
    }
    const linkedCodes = new Set(this.pageData().companyGeneratorMappings[companyCode] || []);
    return this.generatorCatalog().filter((item) => !linkedCodes.has(item.code));
  });
  readonly filteredAvailableGenerators = computed(() => {
    const search = this.availableGeneratorSearch().trim().toLowerCase();
    return this.availableGenerators().filter((item) =>
      !search || [item.code, item.name, item.technology].some((value) => value.toLowerCase().includes(search))
    );
  });
  readonly filteredLinkedGenerators = computed(() => {
    const search = this.linkedGeneratorSearch().trim().toLowerCase();
    return this.linkedGenerators().filter((item) =>
      !search || [item.code, item.name, item.technology].some((value) => value.toLowerCase().includes(search))
    );
  });

  readonly filteredRows = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();
    const selectedQca = this.selectedQca();
    const selectedPss = this.selectedPss();
    const selectedTechnology = this.selectedTechnology();
    const selectedStatus = this.selectedStatus();
    const minCapacity = Number(this.minCapacity());
    const maxCapacity = Number(this.maxCapacity());
    const hasMin = this.minCapacity().trim() !== '' && !Number.isNaN(minCapacity);
    const hasMax = this.maxCapacity().trim() !== '' && !Number.isNaN(maxCapacity);

    return this.rows().filter((row) => {
      const matchesSearch = !search || [row.code, row.name, row.qcaName, row.pssName].some((value) => value.toLowerCase().includes(search));
      const matchesQca = selectedQca === 'All' || row.qcaName === selectedQca;
      const matchesPss = selectedPss === 'All' || row.pssName === selectedPss;
      const matchesTechnology = selectedTechnology === 'All' || row.technology === selectedTechnology;
      const matchesStatus = selectedStatus === 'All' || row.status === selectedStatus;
      const matchesMin = !hasMin || row.ratedCapacityMw >= minCapacity;
      const matchesMax = !hasMax || row.ratedCapacityMw <= maxCapacity;

      return matchesSearch && matchesQca && matchesPss && matchesTechnology && matchesStatus && matchesMin && matchesMax;
    });
  });

  goToPssDashboard(pssCode: string): void {
    void this.router.navigate(['/pss-dashboard', pssCode]);
  }

  goToQcaDashboard(qcaCode: string): void {
    void this.router.navigate(['/qca-dashboard'], { queryParams: { qcaCode } });
  }

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRows().length / this.pageSize())));
  readonly paginatedRows = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });
  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly showingText = computed(() => {
    const total = this.filteredRows().length;
    if (total === 0) {
      return 'Showing 0 entries';
    }
    const start = ((this.currentPage() - 1) * this.pageSize()) + 1;
    const end = Math.min(this.currentPage() * this.pageSize(), total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });

  constructor() {
    this.http.get<GeneratingCompanyManagementMock>('json/generating-company-management.mock.json').subscribe({
      next: (data) => this.pageData.set(data),
      error: () => this.pageData.set(DEFAULT_DATA)
    });
  }

  updateSearch(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  updateQca(value: string): void {
    this.selectedQca.set(value);
    this.currentPage.set(1);
  }

  updatePss(value: string): void {
    this.selectedPss.set(value);
    this.currentPage.set(1);
  }

  updateTechnology(value: string): void {
    this.selectedTechnology.set(value);
    this.currentPage.set(1);
  }

  updateStatus(value: string): void {
    this.selectedStatus.set(value);
    this.currentPage.set(1);
  }

  updateMinCapacity(value: string): void {
    this.minCapacity.set(value);
    this.currentPage.set(1);
  }

  updateMaxCapacity(value: string): void {
    this.maxCapacity.set(value);
    this.currentPage.set(1);
  }

  resetFilters(): void {
    this.searchTerm.set('');
    this.selectedQca.set('All');
    this.selectedPss.set('All');
    this.selectedTechnology.set('All');
    this.selectedStatus.set('All');
    this.minCapacity.set('');
    this.maxCapacity.set('');
    this.currentPage.set(1);
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
  }

  openManageGeneratorsModal(companyCode: string): void {
    this.selectedCompanyCode.set(companyCode);
    this.activeGeneratorTab.set('available');
    this.availableGeneratorSearch.set('');
    this.linkedGeneratorSearch.set('');
    this.selectedAvailableGeneratorCodes.set([]);
    this.selectedLinkedGeneratorCodes.set([]);
    this.isManageGeneratorsModalOpen.set(true);
  }

  closeManageGeneratorsModal(): void {
    this.isManageGeneratorsModalOpen.set(false);
    this.selectedAvailableGeneratorCodes.set([]);
    this.selectedLinkedGeneratorCodes.set([]);
  }

  switchGeneratorTab(tab: 'available' | 'linked'): void {
    this.activeGeneratorTab.set(tab);
    this.selectedAvailableGeneratorCodes.set([]);
    this.selectedLinkedGeneratorCodes.set([]);
  }

  updateAvailableGeneratorSearch(value: string): void {
    this.availableGeneratorSearch.set(value);
  }

  updateLinkedGeneratorSearch(value: string): void {
    this.linkedGeneratorSearch.set(value);
  }

  toggleAvailableGeneratorSelection(generatorCode: string, checked: boolean): void {
    const current = this.selectedAvailableGeneratorCodes();
    this.selectedAvailableGeneratorCodes.set(
      checked ? [...current, generatorCode] : current.filter((code) => code !== generatorCode)
    );
  }

  toggleLinkedGeneratorSelection(generatorCode: string, checked: boolean): void {
    const current = this.selectedLinkedGeneratorCodes();
    this.selectedLinkedGeneratorCodes.set(
      checked ? [...current, generatorCode] : current.filter((code) => code !== generatorCode)
    );
  }

  addSelectedGenerators(): void {
    const companyCode = this.selectedCompanyCode();
    if (!companyCode || this.selectedAvailableGeneratorCodes().length === 0) {
      return;
    }

    const selected = new Set(this.selectedAvailableGeneratorCodes());
    this.pageData.update((data) => {
      const currentMappings = data.companyGeneratorMappings[companyCode] || [];
      const nextMappings = Array.from(new Set([...currentMappings, ...selected]));
      return {
        ...data,
        companyGeneratorMappings: {
          ...data.companyGeneratorMappings,
          [companyCode]: nextMappings
        }
      };
    });
    this.selectedAvailableGeneratorCodes.set([]);
    this.activeGeneratorTab.set('linked');
  }

  removeSelectedGenerators(): void {
    const companyCode = this.selectedCompanyCode();
    if (!companyCode || this.selectedLinkedGeneratorCodes().length === 0) {
      return;
    }

    const selected = new Set(this.selectedLinkedGeneratorCodes());
    this.pageData.update((data) => ({
      ...data,
      companyGeneratorMappings: {
        ...data.companyGeneratorMappings,
        [companyCode]: (data.companyGeneratorMappings[companyCode] || []).filter((generatorCode) => !selected.has(generatorCode))
      }
    }));
    this.selectedLinkedGeneratorCodes.set([]);
  }

  removeLinkedGenerator(generatorCode: string): void {
    const companyCode = this.selectedCompanyCode();
    if (!companyCode) {
      return;
    }

    this.pageData.update((data) => ({
      ...data,
      companyGeneratorMappings: {
        ...data.companyGeneratorMappings,
        [companyCode]: (data.companyGeneratorMappings[companyCode] || []).filter((item) => item !== generatorCode)
      }
    }));
    this.selectedLinkedGeneratorCodes.set(this.selectedLinkedGeneratorCodes().filter((item) => item !== generatorCode));
  }

  formatCapacity(value: number): string {
    return new Intl.NumberFormat('en-IN').format(value);
  }
}
